// generateStatementPDF.ts — Sprint 13. ~320 LOC of jsPDF rendering
// extracted from Statements.tsx (which had it inline). Pure module:
// no React, no antd, no store. Drop-in helper that just takes the
// data and triggers a download.
//
// Why this lives outside the page:
//   * It's the heaviest single block on the old page (320 LOC).
//   * It's pure rendering — no UI state, no hooks. Tests can pin
//     small slices (palette resolution, due-date formatting) without
//     mounting React.
//   * Importing jspdf + jspdf-autotable is expensive; isolating the
//     helper makes it easy to lazy-load later if bundle size becomes
//     an issue.
//
// Sprint 146 (R14-4, ADR-141) — the `fees: any[]` parameter is GONE. This
// module used to re-derive the owner's lines from the page's whole fee list
// with `f.ownerId === ownerKey && f.period === statement.period`, the same
// duplicated rule that emptied the preview modal: `POST /api/fees` never sets
// `period`, so an app-created fee is `null` where StatementService buckets it
// under `""`. The PDF is the artefact a manager actually hands an owner, so it
// shipped a balance with an empty Fee Details table and a $0.00 "Total Fees
// Charged". Lines now come from `statement.fees` — the server's own list.
//
// Sprint 146 (R14-11) — every money value routes through `currency()`
// (en-CA/CAD, thousands-separated). This file had nine bare `$` + toFixed(2)
// templates, so `$1900.00` went out on the document itself.
import dayjs from 'dayjs';
import { currency } from '../../utils/currency';

declare module 'jspdf' {
  interface jsPDF { lastAutoTable: { finalY: number }; }
}

/** Renders a single owner statement and triggers a browser download.
 *  `statement`/`owner` are kept as `any` to match the loose store
 *  shape upstream — tightening these would force a backend type
 *  re-export, which is out of scope for the decomposition sprint. */
export async function generateStatementPDF(
  statement: any,
  owner:     any,
  condoName: string,
): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF({ unit: 'mm', format: 'a4', compress: true });

  // ── Palette (light, professional) ─────────────────────────────────
  const INDIGO  = [79, 70, 229]   as [number, number, number]; // #4f46e5
  const INK     = [17, 24, 39]    as [number, number, number]; // #111827
  const SLATE   = [100, 116, 139] as [number, number, number]; // #64748b
  const BORDER  = [229, 231, 235] as [number, number, number]; // #e5e7eb
  const SURFACE = [248, 249, 255] as [number, number, number]; // #f8f9ff
  const WHITE   = [255, 255, 255] as [number, number, number];
  const GREEN   = [5, 150, 105]   as [number, number, number]; // #059669
  const RED     = [220, 38, 38]   as [number, number, number]; // #dc2626
  const AMBER   = [217, 119, 6]   as [number, number, number]; // #d97706

  const PAGE_W = 210;
  const PAGE_H = 297;
  const M      = 18;
  const CW     = PAGE_W - M * 2;

  // ── Top accent rule ───────────────────────────────────────────────
  doc.setFillColor(...INDIGO);
  doc.rect(0, 0, PAGE_W, 3, 'F');

  // ── Header ────────────────────────────────────────────────────────
  let y = 12;
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK);
  doc.text(condoName, M, y);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...SLATE);
  doc.text('Property Management', M, y + 5.5);

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INDIGO);
  doc.text('ACCOUNT STATEMENT', PAGE_W - M, y, { align: 'right' });

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...SLATE);
  doc.text(`Period: ${statement.period}`, PAGE_W - M, y + 6, { align: 'right' });
  doc.text(`Issued: ${dayjs().format('MMMM D, YYYY')}`, PAGE_W - M, y + 11, { align: 'right' });

  // ── Divider ───────────────────────────────────────────────────────
  y = 30;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.line(M, y, PAGE_W - M, y);

  // ── Recipient + statement meta ────────────────────────────────────
  y = 36;

  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...SLATE);
  doc.text('PREPARED FOR', M, y);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK);
  doc.text(owner?.name || '—', M, y + 6);

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...SLATE);
  doc.text(`Unit ${owner?.unitNumber || '—'}`, M, y + 12);
  if (owner?.email) doc.text(owner.email, M, y + 17);

  const metaX = 130;
  const metaRows: [string, string][] = [
    ['Statement ID', String(statement.id || '').slice(0, 20)],
    ['Period',       statement.period || '—'],
    ['Due Date',     statement.dueDate ? dayjs(statement.dueDate).format('MMM D, YYYY') : '—'],
    ['Generated',    dayjs().format('MMM D, YYYY')],
  ];

  metaRows.forEach((row, i) => {
    const ry = y + i * 6.5;
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...SLATE);
    doc.text(row[0], metaX, ry);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...INK);
    doc.text(row[1], PAGE_W - M, ry, { align: 'right' });
  });

  // ── Divider ───────────────────────────────────────────────────────
  y = 66;
  doc.setDrawColor(...BORDER);
  doc.line(M, y, PAGE_W - M, y);

  // ── Summary cards (3 columns) ─────────────────────────────────────
  y = 72;
  const cardW = (CW - 8) / 3;
  const balanceColor: [number, number, number] = statement.currentBalance > 0 ? RED : GREEN;

  const cards = [
    { label: 'PREVIOUS BALANCE',  value: currency(statement.previousBalance), accent: INDIGO       },
    { label: 'PAYMENTS RECEIVED', value: currency(statement.payments),        accent: GREEN        },
    { label: 'BALANCE DUE',       value: currency(statement.currentBalance),  accent: balanceColor },
  ];

  cards.forEach((card, i) => {
    const cx = M + i * (cardW + 4);

    doc.setFillColor(...WHITE);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.25);
    doc.roundedRect(cx, y, cardW, 22, 1.5, 1.5, 'FD');

    // Top accent bar
    doc.setFillColor(...card.accent);
    doc.roundedRect(cx, y, cardW, 3, 1.5, 1.5, 'F');
    doc.rect(cx, y + 1.5, cardW, 1.5, 'F');

    // Label
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...SLATE);
    doc.text(card.label, cx + cardW / 2, y + 9, { align: 'center' });

    // Value
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(card.accent[0], card.accent[1], card.accent[2]);
    doc.text(card.value, cx + cardW / 2, y + 18, { align: 'center' });
  });

  y = 100;

  // ── Fee Details table ─────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK);
  doc.text('Fee Details', M, y);

  doc.setDrawColor(...INDIGO);
  doc.setLineWidth(0.5);
  doc.line(M, y + 1.5, M + 24, y + 1.5);

  y += 5;

  // R14-4 — the server's own list, not a client re-derivation. See the header.
  const ownerFees: any[] = Array.isArray(statement.fees) ? statement.fees : [];

  const statusColor = (status: string): [number, number, number] => {
    if (status === 'PAID')    return GREEN;
    if (status === 'OVERDUE') return RED;
    return AMBER;
  };

  if (ownerFees.length > 0) {
    autoTable(doc, {
      startY: y,
      head:   [['Description', 'Type', 'Due Date', 'Amount', 'Status']],
      body:   ownerFees.map((f: any) => [
        f.description || '—',
        (f.type || '—').replace(/-/g, ' '),
        f.dueDate ? dayjs(f.dueDate).format('MMM D, YYYY') : '—',
        currency(f.amount),
        (f.status || '—'),
      ]),
      margin: { left: M, right: M },
      styles: {
        fontSize:    8.5,
        cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
        textColor:   INK,
        lineColor:   BORDER,
        lineWidth:   0.2,
      },
      headStyles: {
        fillColor:  SURFACE,
        textColor:  INDIGO,
        fontStyle:  'bold',
        fontSize:   7.5,
        lineColor:  BORDER,
        lineWidth:  0.2,
      },
      alternateRowStyles: { fillColor: [252, 252, 254] as [number, number, number] },
      columnStyles: {
        0: { cellWidth: 56 },
        1: { cellWidth: 30 },
        2: { cellWidth: 32 },
        3: { cellWidth: 28, halign: 'right' as const },
        4: { cellWidth: 28 },
      },
      didParseCell: (data) => {
        if (data.column.index === 4 && data.section === 'body') {
          const status = String(data.cell.raw || '');
          const col    = statusColor(status);
          data.cell.styles.textColor = col;
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });
    y = doc.lastAutoTable.finalY + 10;
  } else {
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...SLATE);
    doc.text('No fee details available for this period.', M, y + 6);
    y += 16;
  }

  // ── Account Summary (right-aligned) ───────────────────────────────
  const totalFees = ownerFees.reduce((s: number, f: any) => s + Number(f.amount), 0);
  const summaryRows: [string, string, boolean][] = [
    ['Previous Balance',   currency(statement.previousBalance),   false],
    ['Total Fees Charged', currency(totalFees),                   false],
    ['Payments Received',  `(${currency(statement.payments)})`,   false],
    ['Balance Due',        currency(statement.currentBalance),    true ],
  ];

  const summaryX = PAGE_W - M - 80;
  const summaryW = 80;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK);
  doc.text('Account Summary', summaryX, y);
  doc.setDrawColor(...INDIGO);
  doc.setLineWidth(0.5);
  doc.line(summaryX, y + 1.5, summaryX + 38, y + 1.5);
  y += 5;

  doc.setFillColor(...WHITE);
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.25);
  doc.roundedRect(summaryX, y, summaryW, summaryRows.length * 7 + 2, 1.5, 1.5, 'FD');

  summaryRows.forEach((row, i) => {
    const ry     = y + 5 + i * 7;
    const isLast = row[2];

    if (isLast) {
      doc.setFillColor(...SURFACE);
      doc.rect(summaryX, ry - 4, summaryW, 7, 'F');
      doc.setDrawColor(...INDIGO);
      doc.setLineWidth(0.2);
      doc.line(summaryX, ry - 4, summaryX + summaryW, ry - 4);
    }

    doc.setFontSize(isLast ? 8.5 : 8);
    doc.setFont('helvetica', isLast ? 'bold' : 'normal');
    doc.setTextColor(
      isLast ? INK[0]   : SLATE[0],
      isLast ? INK[1]   : SLATE[1],
      isLast ? INK[2]   : SLATE[2],
    );
    doc.text(row[0], summaryX + 4, ry);

    const valColor: [number, number, number] = isLast
      ? (statement.currentBalance > 0 ? RED : GREEN)
      : INK;
    doc.setTextColor(...valColor);
    doc.setFont('helvetica', isLast ? 'bold' : 'normal');
    doc.text(row[1], summaryX + summaryW - 4, ry, { align: 'right' });
  });

  y += summaryRows.length * 7 + 10;

  // ── Payment-due note ──────────────────────────────────────────────
  if (statement.currentBalance > 0) {
    doc.setFillColor(254, 252, 232); // amber-50
    doc.setDrawColor(253, 230, 138); // amber-200
    doc.setLineWidth(0.2);
    doc.roundedRect(M, y, CW, 12, 1.5, 1.5, 'FD');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...AMBER);
    doc.text('Payment Due', M + 4, y + 5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 80, 10);
    doc.text(
      `Please remit ${currency(statement.currentBalance)} by the due date shown above. Contact management for payment options.`,
      M + 28, y + 5,
    );
    y += 18;
  }

  // ── Footer ────────────────────────────────────────────────────────
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.line(M, PAGE_H - 16, PAGE_W - M, PAGE_H - 16);

  doc.setFillColor(...INDIGO);
  doc.circle(M,         PAGE_H - 16, 0.6, 'F');
  doc.circle(PAGE_W - M, PAGE_H - 16, 0.6, 'F');

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...SLATE);
  doc.text(`${condoName}  ·  Statement for ${statement.period}`, M, PAGE_H - 9);
  doc.text(
    `Generated ${dayjs().format('YYYY-MM-DD')}  ·  Page 1 of 1`,
    PAGE_W - M, PAGE_H - 9, { align: 'right' },
  );

  doc.save(
    `Statement_${owner?.name?.replace(/\s/g, '_') || 'Unknown'}_${statement.period}_${dayjs().format('YYYYMMDD')}.pdf`,
  );
}
